import React, { useState } from "react";
import { ChartData } from "chart.js";
import { Line } from "react-chartjs-2";

import { ChartContainer, Title, RangeButtonGroup, RangeButton } from "./styled";
import { chartOptions, createAreaGradient } from "./chartConfig";
import { color } from "style/constants";

export interface ScorePoint {
  scoredAt: string;
  score: number;
}

export type TimeRange = "YTD" | "3 Months" | "All Time";

export const TIME_RANGES: TimeRange[] = ["3 Months", "YTD", "All Time"];

const filterPoints = (
  points: readonly ScorePoint[],
  range: TimeRange,
  now: Date
): ScorePoint[] => {
  if (range === "All Time") return [...points];
  if (range === "YTD") {
    const start = new Date(now.getFullYear(), 0, 1);
    return points.filter(p => new Date(p.scoredAt) >= start);
  }
  // 3 Months
  const start = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
  return points.filter(p => new Date(p.scoredAt) >= start);
};

const formatXLabel = (iso: string): string => {
  const d = new Date(iso);
  const month = d.toLocaleDateString("en-US", {
    month: "short",
    timeZone: "UTC"
  });
  return `${month} ${d.getUTCDate()}`;
};

interface ScoreHistoryChartProps {
  points: readonly ScorePoint[];
}

const ScoreHistoryChart: React.FC<ScoreHistoryChartProps> = ({ points }) => {
  const [range, setRange] = useState<TimeRange>("YTD");

  const filtered = filterPoints(points, range, new Date());

  const labels = filtered.map(p => formatXLabel(p.scoredAt));
  const scores = filtered.map(p => p.score);

  const data: ChartData<"line"> = {
    labels,
    datasets: [
      {
        data: scores,
        borderColor: color.SCORE_CHART_LINE,
        backgroundColor: context => {
          const chart = context.chart;
          const { ctx, chartArea } = chart;
          if (!chartArea) return color.SCORE_CHART_LINE;
          return createAreaGradient(ctx, chartArea);
        },
        fill: true,
        pointBackgroundColor: color.SCORE_CHART_LINE,
        pointBorderColor: color.SCORE_CHART_LINE
      }
    ]
  };

  return (
    <ChartContainer>
      <Title>Pulse Score History</Title>

      <RangeButtonGroup>
        {TIME_RANGES.map(r => (
          <RangeButton key={r} active={range === r} onClick={() => setRange(r)}>
            {r === "3 Months" ? "3M" : r}
          </RangeButton>
        ))}
      </RangeButtonGroup>

      <Line data={data} options={chartOptions} />
    </ChartContainer>
  );
};

export default ScoreHistoryChart;
